import React, { useCallback, useEffect, useState } from 'react';

import {
  pendingReviewParamKey,
  reminderAutomationIdParamKey,
  reminderReportIdParamKey,
} from '@aline/common';
import { link } from '@aline/routes';
import type { GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-premium';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { ListTodo } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlineCheckbox, ContentWrapper, ListEmpty, ReportFiltersPanel } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useMaybeReminderParams } from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  useGetPendingReviewCount,
  useGetReminders,
  useRemindersSubscription,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import type { ReminderListRow } from './ColumnDef';
import { useRemindersColumnDefs } from './ColumnDef';
import { RemindersProvider } from './RemindersContext';
import { RemindersFloatingActionBar } from './RemindersFloatingActionBar';
import type { RemindersLocalState } from './toolbar';
import { RemindersToolBar } from './toolbar';
import { useClientSideReminderFilters } from './useFilteredReminders';

const useStyles = makeStyles()(theme => ({
  content: {
    position: 'relative',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 400,
    },
    '& .MuiDataGrid-main': {
      border: `1px solid ${theme.appColors.rule}`,
      borderRadius: 0,
    },
    '& .MuiDataGrid-row': {
      '&:hover #overflow-menu': {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

export function Reminders() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { columnDefs } = useRemindersColumnDefs();
  const { reminderId } = useMaybeReminderParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeFilters = getRemindersRouteFilters(searchParams);
  const [previousRouteFilters, setPreviousRouteFilters] = useState(routeFilters);
  const remindersStateTuple = useState<RemindersLocalState>(() => ({
    filter: Apollo.ReminderFilter.Incomplete,
    filterAssignees: [],
    filterAutomationId: null,
    filterReportId: null,
    filterTypes: [],
    searchString: '',
    ...routeFilters,
  }));
  const [remindersState, setRemindersState] = remindersStateTuple;

  /**
   * Apply changed link filters before rendering the grid. Unchanged params must
   * preserve manual edits across tab switches; preview routes omit the params.
   */
  if (
    routeFilters?.filter !== previousRouteFilters?.filter ||
    routeFilters?.filterAutomationId !== previousRouteFilters?.filterAutomationId ||
    routeFilters?.filterReportId !== previousRouteFilters?.filterReportId
  ) {
    setPreviousRouteFilters(routeFilters);
    if (routeFilters) setRemindersState(state => ({ ...state, ...routeFilters }));
  }

  const { pendingReviewCount } = useGetPendingReviewCount();
  const { loading, reminders } = useGetReminders({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: remindersState.filter,
    },
  });
  const isPendingReviewFilterActive = remindersState.filter === Apollo.ReminderFilter.PendingReview;

  useRemindersSubscription();

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'dueDate' satisfies keyof ReminderListRow, sort: 'asc' },
  ]);
  const [selectedReminderIds, setSelectedReminderIds] = useState<GridRowSelectionModel>({
    ids: new Set([]),
    type: 'include',
  });
  const { rows } = useClientSideReminderFilters({
    reminders,
    remindersState,
  });

  const handleClearPendingReviewFilter = useCallback(() => {
    setRemindersState(s => ({
      ...s,
      filter: Apollo.ReminderFilter.Incomplete,
      filterAutomationId: null,
      filterReportId: null,
    }));
    setSearchParams({}, { replace: true });
  }, [setRemindersState, setSearchParams]);

  const handleTogglePendingReviewFilter = useCallback(() => {
    if (isPendingReviewFilterActive) {
      handleClearPendingReviewFilter();
      return;
    }

    setRemindersState(s => ({
      ...s,
      filter: Apollo.ReminderFilter.PendingReview,
    }));
  }, [handleClearPendingReviewFilter, isPendingReviewFilterActive, setRemindersState]);

  useEffect(() => {
    if (reminderId && !loading) {
      setSelectedReminderIds({ ids: new Set([reminderId]), type: 'include' });
      navigate(link.RemindersPreview({ reminderId }), { replace: true });
    }
  }, [reminderId, loading, navigate]);

  return (
    <RemindersProvider remindersStateTuple={remindersStateTuple}>
      <ContentWrapper className={classes.content}>
        <div className={classes.listContainer}>
          <DataGridPremium<ReminderListRow>
            checkboxSelection
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            showToolbar
            apiRef={apiRef}
            className={classes.xGrid}
            columnHeaderHeight={40}
            columns={columnDefs}
            getRowHeight={() => 'auto'}
            loading={loading}
            rowSelectionModel={selectedReminderIds}
            rows={rows}
            slotProps={{
              filterPanel: {
                filterableColumns: 'visible',
              },
              loadingOverlay: {
                noRowsVariant: 'linear-progress',
                variant: 'linear-progress',
              },
              panel: {
                placement: 'bottom-start',
              },
              toolbar: {
                reminderAlertsProps: {
                  isPendingReviewFilterActive,
                  onClearPendingReviewFilter: handleClearPendingReviewFilter,
                  onViewPendingReview: handleTogglePendingReviewFilter,
                  pendingReviewCount,
                },
                remindersStateTuple,
              },
            }}
            slots={{
              baseCheckbox: AlineCheckbox,
              filterPanel: ReportFiltersPanel,
              footer: RemindersFloatingActionBar,
              noRowsOverlay: RemindersListEmpty,
              toolbar: RemindersToolBar,
            }}
            sortModel={sortModel}
            sx={createDataGridPremiumPaperStyles}
            onRowClick={({ id: rowReminderId }) => {
              navigate(link.RemindersPreview({ reminderId: String(rowReminderId) }), {
                replace: true,
              });
            }}
            onRowSelectionModelChange={setSelectedReminderIds}
            onSortModelChange={setSortModel}
          />
        </div>
      </ContentWrapper>
    </RemindersProvider>
  );
}

function getRemindersRouteFilters(
  searchParams: URLSearchParams
): Pick<RemindersLocalState, 'filter' | 'filterAutomationId' | 'filterReportId'> | null {
  const reportIdParam = searchParams.get(reminderReportIdParamKey);
  const pendingReviewParam = searchParams.get(pendingReviewParamKey);
  const automationIdParam = searchParams.get(reminderAutomationIdParamKey);

  if (reportIdParam === null && pendingReviewParam === null && automationIdParam === null)
    return null;

  return {
    filter:
      pendingReviewParam === 'true'
        ? Apollo.ReminderFilter.PendingReview
        : Apollo.ReminderFilter.Incomplete,
    filterAutomationId: automationIdParam,
    filterReportId: reportIdParam,
  };
}

function RemindersListEmpty(): JSX.Element {
  return (
    <ListEmpty
      icon={<ListTodo size={32} />}
      subtitle="Click add to create a new reminder or change your filters."
      title="No reminders"
    />
  );
}
